import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  HIRING_CHANNEL_MAP,
  jobApplyUrl,
  type HiringChannel,
} from "@goyone/shared";
import { Badge, ErrorState, Notice, PageHead, Stat } from "../../components/ui";
import { Icon } from "../../components/Icon";
import { Fact, FactGrid } from "../../components/staff";
import { useToast } from "../../components/Toast";
import { api } from "../../lib/api";
import { date, money, num, plural, WORDS } from "../../lib/format";
import { useSession } from "../../auth/AuthProvider";

interface CampaignJob {
  id: string;
  code: string | null;
  title: string;
  slug: string;
  status: string;
  statusLabel: string;
  statusKind: string;
  accepting: boolean;
  openings: number;
  form: { id: string; name: string } | null;
  applications: number;
  hired: number;
}

interface SourceRow {
  key: string;
  label: string;
  applied: number;
  hired: number;
}

interface CampaignView {
  id: string;
  name: string;
  goal: number;
  status: string;
  statusLabel: string;
  statusKind: string;
  startsOn: string | null;
  endsOn: string | null;
  notes: string | null;
  purpose: string | null;
  budget: number | null;
  channels: HiringChannel[];
  ownerId: string | null;
  jobs: CampaignJob[];
  results: {
    openings: number;
    applications: number;
    active: number;
    rejected: number;
    hired: number;
    goalPercent: number | null;
    costPerHire: number | null;
    medianDaysToHire: number | null;
    bySource: SourceRow[];
  };
}

/**
 * صفحة الحملة.
 *
 * ## ولماذا النتائجُ صدرُ الصفحة
 *
 * من يفتح حملةً قائمةً يسأل «هل تمشي؟»، ومن يفتح حملةً منتهيةً يسأل «هل
 * نجحت؟» — والسؤالان جوابُهما رقمٌ واحد: كم عُيّن من الهدف. وعددُ الطلبات
 * ليس جوابًا: حملةٌ جاءها ألفُ طلبٍ ولم تُعيّن أحدًا فاشلة، وأخرى جاءها
 * عشرون فعيّنت خمسةً ناجحة.
 *
 * ## ورابطٌ لكلّ قناة
 *
 * القناةُ لا تُقاس بسؤال «من أين عرفتَ عنّا؟» — يُجاب عنه بالتخمين أو
 * يُترك. فلكلّ قناةٍ رابطُها، ومن يقدّم منه يُسجَّل مصدرُه بلا سؤال. وهذا
 * حدُّ ما يُمكن قياسُه بصدق: لا تكاملَ مع منصّةٍ ولا قراءةَ مشاهداتٍ منها.
 */
export function CampaignDetail() {
  const { id = "" } = useParams();
  const session = useSession();
  const toast = useToast();

  const campaign = useQuery({
    queryKey: ["recruit", "campaign", id],
    queryFn: () => api<CampaignView>(`/hr/recruit/campaigns/${id}`),
  });

  const c = campaign.data;
  const r = c?.results;

  const copy = (url: string) => {
    void navigator.clipboard.writeText(url);
    toast("نُسخ الرابط");
  };

  return (
    <div className="page">
      <PageHead
        title={c?.name ?? "الحملة"}
        subtitle={
          c
            ? `${date(c.startsOn ?? "")}${c.endsOn ? ` — ${date(c.endsOn)}` : " — مفتوحة"}`
            : undefined
        }
        actions={
          <Link className="btn" to="/hr/recruit/campaigns">
            <Icon name="arrowLeft" size={15} />
            كل الحملات
          </Link>
        }
      />

      {campaign.isError && (
        <ErrorState
          error={campaign.error}
          onRetry={() => void campaign.refetch()}
        />
      )}

      {campaign.isPending && (
        <div className="skeleton" style={{ height: 260 }} />
      )}

      {c && r && (
        <>
          <div className="stats">
            <Stat
              label="عُيّن من الهدف"
              value={`${num(r.hired)} / ${num(c.goal)}`}
              note={
                r.goalPercent === null
                  ? undefined
                  : `${num(r.goalPercent)}٪ من الهدف`
              }
            />
            <Stat
              label="الطلبات"
              value={num(r.applications)}
              note={
                r.applications > 0
                  ? `${num(r.active)} ما زالت تتحرّك · ${num(r.rejected)} استُبعدت`
                  : "لم يصل طلبٌ بعد"
              }
            />
            <Stat
              label="كلفة التعيين الواحد"
              value={r.costPerHire === null ? "—" : money(r.costPerHire)}
              note={
                c.budget === null
                  ? "بلا ميزانيةٍ مرصودةٍ لا تُحسب"
                  : r.hired === 0
                    ? "لم يُعيَّن أحدٌ بعد — الميزانيةُ حتى الآن خسارةٌ لا كلفةُ تعيين"
                    : `من ميزانية ${money(c.budget)}`
              }
            />
            <Stat
              label="وسيط أيام التعيين"
              value={
                r.medianDaysToHire === null ? "—" : num(r.medianDaysToHire)
              }
              note={
                r.medianDaysToHire === null
                  ? "يُحسب بعد أوّل تعيين"
                  : "من التقديم إلى القرار — بالوسيط لا بالمتوسّط"
              }
            />
          </div>

          <div className="card">
            <div className="card-head">
              <h2 className="card-title">خطة الحملة</h2>
              <Badge kind={c.statusKind}>{c.statusLabel}</Badge>
            </div>
            <div className="card-body">
              <FactGrid>
                <Fact
                  icon="target"
                  label="الهدف"
                  value={`${num(c.goal)} تعيينًا`}
                />
                <Fact
                  icon="briefcase"
                  label="الوظائف"
                  value={plural(c.jobs.length, WORDS.job)}
                />
                <Fact
                  icon="users"
                  label="الشواغر المعلنة"
                  value={num(r.openings)}
                />
                <Fact
                  icon="wallet"
                  label="الميزانية"
                  value={c.budget === null ? "—" : money(c.budget)}
                />
              </FactGrid>

              {c.purpose && (
                <div className="mt-4">
                  <span className="label">الحاجة التي فُتحت لأجلها</span>
                  <p className="rc-text">{c.purpose}</p>
                </div>
              )}

              {c.notes && (
                <div className="mt-4">
                  <span className="label">ملاحظات</span>
                  <p className="rc-text">{c.notes}</p>
                </div>
              )}

              {/*
                الشاغرُ المعلنُ قد يزيد على الهدف أو ينقص عنه، وكلاهما وارد:
                تُفتح خمسةُ شواغرَ والهدفُ ثلاثةٌ لأنّ اثنين احتياط. ويُقال
                حين يقلّ، لأنّ هدفًا لا شواغرَ تكفيه لا يُبلَغ مهما كان
                التوظيف جيّدًا.
              */}
              {r.openings < c.goal && (
                <Notice
                  title="الشواغر أقلّ من الهدف"
                  icon="alert"
                  kind="warning"
                >
                  الهدفُ {num(c.goal)} تعيينًا، والوظائفُ المعلنةُ تحمل{" "}
                  {num(r.openings)} شاغرًا فقط. لن يُبلغ الهدفُ ولو نجح كلُّ
                  تقديم — افتح وظيفةً أخرى أو زد شواغرَ القائمة.
                </Notice>
              )}
            </div>
          </div>

          {/* ---------- القنوات وروابطها ---------- */}

          <div className="card">
            <div className="card-head">
              <h2 className="card-title">القنوات وروابطها</h2>
            </div>
            <div className="card-body">
              {c.channels.length === 0 ? (
                <p className="tiny mute">
                  لم تُحدَّد قنواتٌ لهذه الحملة. حدّدها من «تعديل» ليظهر لكلّ
                  قناةٍ رابطُها — ومن يقدّم منه يُسجَّل مصدرُه بلا أن يُسأل.
                </p>
              ) : c.jobs.length === 0 ? (
                <p className="tiny mute">
                  لا وظيفةَ في الحملة بعد، والروابطُ تُشتقّ من الوظائف — اربط
                  وظيفةً بهذه الحملة من شاشة الوظائف.
                </p>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>الوظيفة</th>
                        {c.channels.map((k) => (
                          <th key={k}>{HIRING_CHANNEL_MAP[k].label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {c.jobs.map((j) => (
                        <tr key={j.id}>
                          <td>
                            <Link
                              className="cell-strong link"
                              to={`/hr/recruit/jobs/${j.id}`}
                            >
                              {j.title}
                            </Link>
                            {!j.accepting && (
                              <div className="tiny mute">
                                الرابط لا يقبل تقديمًا الآن
                              </div>
                            )}
                          </td>
                          {c.channels.map((k) => (
                            <td key={k}>
                              <button
                                className="btn btn-sm"
                                onClick={() =>
                                  copy(
                                    jobApplyUrl(
                                      window.location.origin,
                                      session.tenant.slug,
                                      j.slug,
                                      k,
                                    ),
                                  )
                                }
                              >
                                <Icon name="paperclip" size={12} />
                                نسخ
                              </button>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {c.channels.length > 0 && (
                <ul
                  className="tiny mute mt-2"
                  style={{ paddingInlineStart: "1.1rem" }}
                >
                  {c.channels.map((k) => (
                    <li key={k}>
                      <strong>{HIRING_CHANNEL_MAP[k].label}</strong> —{" "}
                      {HIRING_CHANNEL_MAP[k].description}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* ---------- ما وقع فعلًا ---------- */}

          <div className="card">
            <div className="card-head">
              <h2 className="card-title">من أين جاء من قدّم</h2>
            </div>
            <div className="card-body">
              {r.bySource.length === 0 ? (
                <p className="tiny mute">لا طلباتٍ بعد.</p>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>المصدر</th>
                        <th>الطلبات</th>
                        <th>التعيينات</th>
                        <th>نسبة التحوّل</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.bySource.map((x) => (
                        <tr key={x.key}>
                          <td className="cell-strong">{x.label}</td>
                          <td className="num">{num(x.applied)}</td>
                          <td className="num">{num(x.hired)}</td>
                          <td className="num">
                            {/*
                              النسبةُ لا تُحسب على عيّنةٍ صغيرة: ثلاثةُ طلباتٍ
                              منها واحدٌ عُيّن ليست «٣٣٪ تحوّل» — هي صدفة.
                            */}
                            {x.applied >= 10
                              ? `${num(Math.round((x.hired / x.applied) * 100))}٪`
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <p className="tiny mute mt-2">
                يُقرأ من قناة كلّ مرشّح لا من قائمة القنوات أعلاه: تلك خطّةٌ
                كُتبت قبل النشر، وهذا ما وقع. ومن قدّم قبل أن تُحفظ القناةُ يظهر
                تحت اسم مصدره العامّ. ونسبةُ التحوّل لا تُعرض تحت عشرة طلبات —
                ثلاثةٌ منها واحدٌ عُيّن ليست ثلاثًا وثلاثين بالمئة.
              </p>
            </div>
          </div>

          {/* ---------- الوظائف واستماراتها ---------- */}

          <div className="card">
            <div className="card-head">
              <h2 className="card-title">وظائف الحملة</h2>
              <span style={{ marginInlineStart: "auto" }}>
                <Link className="btn btn-sm" to="/hr/recruit/jobs">
                  <Icon name="plus" size={13} />
                  اربط وظيفة
                </Link>
              </span>
            </div>
            <div className="card-body">
              {c.jobs.length === 0 ? (
                <p className="tiny mute">
                  لا وظائفَ بعد. الوظيفةُ تُربط بالحملة من نموذجها في شاشة
                  الوظائف — تُختار الحملةُ هناك، لا تُضاف الوظيفةُ هنا. ومصدرٌ
                  واحدٌ للربط يمنع أن يفترق الجانبان.
                </p>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>الوظيفة</th>
                        <th>الاستمارة</th>
                        <th>الشواغر</th>
                        <th>الطلبات</th>
                        <th>عُيّن</th>
                        <th>الحالة</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {c.jobs.map((j) => (
                        <tr key={j.id}>
                          <td>
                            <Link
                              className="cell-strong link"
                              to={`/hr/recruit/jobs/${j.id}`}
                            >
                              {j.title}
                            </Link>
                            <div className="tiny mute num">{j.code ?? "—"}</div>
                          </td>
                          <td className="cell-mute">
                            {j.form ? (
                              <Link className="link" to="/settings/application-forms">
                                {j.form.name}
                              </Link>
                            ) : (
                              "الهويّة وحدها"
                            )}
                          </td>
                          <td className="num">{num(j.openings)}</td>
                          <td className="num">{num(j.applications)}</td>
                          <td className="num">{num(j.hired)}</td>
                          <td>
                            <Badge kind={j.statusKind}>{j.statusLabel}</Badge>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm"
                              onClick={() =>
                                copy(
                                  jobApplyUrl(
                                    window.location.origin,
                                    session.tenant.slug,
                                    j.slug,
                                  ),
                                )
                              }
                            >
                              رابط عامّ
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/*
            القنواتُ المخطَّطةُ وما جاء منها: يُقالان معًا لأنّ الفارقَ بينهما
            هو الخبر — «خطّطنا لخمسٍ وجاء نصفُ من جاء من واحدة» رقمٌ يغيّر
            ميزانيةَ الإعلان في الحملة التالية.
          */}
          <Notice title="ما يُقاس وما لا يُقاس" icon="info">
            القناةُ تُقاس بمن قدّم من رابطها، وكلُّ قناةٍ على حدة: لينكدإن غيرُ
            فيسبوك وإن كان مصدرُهما في ملفّ المرشّح واحدًا. ولا تُقرأ مشاهداتٌ
            ولا نقراتٌ من أيّ منصّة — لا تكاملَ مع فيسبوك ولا لينكدإن ولا
            غيرِهما، فالرابطُ هو الجسرُ الوحيد. ومن قدّم من رابطٍ عامٍّ يُسجَّل
            مصدرُه «رابط الوظيفة» ولا يُنسب إلى قناةٍ بالتخمين.
          </Notice>
        </>
      )}
    </div>
  );
}
